import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/utils";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import promoteYourStore from "@faire/web-api--source/routes/www/open-with-faire/academy/promote-your-store";
import { lazy } from "react";
import * as React from "react";

const LazyBuildingAWebsite = lazy(() =>
  import(
    /* webpackChunkName: "promote-your-store-building-a-website" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/PromoteYourStore/__internal__/BuildingAWebsite"
  ).then((m) => ({ default: m.BuildingAWebsite }))
);
const LazyMarketing = lazy(() =>
  import(
    /* webpackChunkName: "promote-your-store-marketing" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/PromoteYourStore/__internal__/Marketing"
  ).then((m) => ({ default: m.Marketing }))
);
const LazyYourGrandOpening = lazy(() =>
  import(
    /* webpackChunkName: "promote-your-store-your-grand-opening" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/PromoteYourStore/__internal__/YourGrandOpening"
  ).then((m) => ({ default: m.YourGrandOpening }))
);

const C1 = "Building a website";
const BUILDING_A_WEBSITE: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.BUILDING_A_WEBSITE,
  title: C1,
  readTimeMinutes: 5,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyBuildingAWebsite,
};

const C2 = "Marketing";
const MARKETING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.MARKETING,
  title: C2,
  readTimeMinutes: 8,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyMarketing,
};

const C3 = "Your grand opening";
const YOUR_GRAND_OPENING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.YOUR_GRAND_OPENING,
  title: C3,
  readTimeMinutes: 3,
  titleHash: convertTitleToHash(C3),
  ChapterComponent: LazyYourGrandOpening,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  BUILDING_A_WEBSITE,
  MARKETING,
  YOUR_GRAND_OPENING,
];

export const PROMOTE_YOUR_STORE: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.PROMOTE_YOUR_STORE,
  title: "Promote your store",
  description:
    "Set up your website and start marketing your store to attract customers.",
  cardSummary:
    "Bring customers through your doors on opening day—and well after.",
  guideSummary: (
    <>
      Attracting your store’s first customers starts with creative
      marketing—both online and within the local community. Get your website up
      and running, and use social media and email marketing to drive traffic to
      your site, as well as your physical storefront. On a local level, post
      flyers around your neighborhood, list your store in local business
      directories, and attend local or sponsor local events. All of this can
      build towards your grand opening. There is no better way to introduce your
      business to the community, boost your profile, and kick-start your
      business.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/8abd80367448ad88aa0ac01d4232601653fa3dae8dc506bc3bb145f006fe2c0f.jpeg",
  featuredImageAltText:
    "Two shop owners standing outside in red shirts holding up peace signs",
  backgroundImage:
    "https://cdn.faire.com/fastly/3569c8ad365d2297cbfa816568f4f9316c2217d42a7bc9371fc0a4f478731897.jpeg",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: promoteYourStore(),
  titleTag: "How to market your store & its grand opening",
};
